import { motion, useReducedMotion } from "framer-motion";
import { memo, useMemo, useState } from "react";

type NodeKey =
  | "User"
  | "Council"
  | "Strategist"
  | "Scholar"
  | "Researcher"
  | "Archives"
  | "Evidence"
  | "Records"
  | "Documents"
  | "CLI"
  | "API"
  | "MCP"
  | "Workspace";

type GraphNode = {
  id: NodeKey;
  x: number;
  y: number;
  r: number;
  tone: "white" | "green" | "cyan";
  delay: number;
};

type Edge = {
  id: string;
  from: NodeKey;
  to: NodeKey;
  d: string;
  delay: number;
  duration: number;
};

const nodes: GraphNode[] = [
  { id: "User", x: 76, y: 218, r: 27, tone: "white", delay: 0 },
  { id: "Workspace", x: 286, y: 418, r: 27, tone: "white", delay: 0.8 },
  { id: "Council", x: 286, y: 218, r: 47, tone: "green", delay: 0.15 },
  { id: "Strategist", x: 492, y: 64, r: 34, tone: "cyan", delay: 0.35 },
  { id: "Scholar", x: 492, y: 218, r: 33, tone: "cyan", delay: 0.55 },
  { id: "Researcher", x: 492, y: 384, r: 34, tone: "cyan", delay: 0.75 },
  { id: "Archives", x: 716, y: 100, r: 34, tone: "green", delay: 0.45 },
  { id: "Evidence", x: 716, y: 218, r: 33, tone: "green", delay: 0.65 },
  { id: "Records", x: 716, y: 360, r: 33, tone: "green", delay: 0.85 },
  { id: "Documents", x: 925, y: 64, r: 34, tone: "green", delay: 0.6 },
  { id: "CLI", x: 925, y: 156, r: 34, tone: "green", delay: 0.8 },
  { id: "API", x: 925, y: 270, r: 34, tone: "green", delay: 1 },
  { id: "MCP", x: 925, y: 405, r: 34, tone: "green", delay: 1.15 },
];

const edges: Edge[] = [
  { id: "user-council", from: "User", to: "Council", d: "M103 218 C150 218 206 218 239 218", delay: 0.1, duration: 4.2 },
  { id: "workspace-council", from: "Workspace", to: "Council", d: "M286 391 C286 338 286 291 286 265", delay: 1.1, duration: 4.8 },
  { id: "council-strategist", from: "Council", to: "Strategist", d: "M333 218 C395 196 392 86 458 64", delay: 0.4, duration: 4.1 },
  { id: "council-scholar", from: "Council", to: "Scholar", d: "M333 218 C380 218 424 218 459 218", delay: 0.7, duration: 3.8 },
  { id: "council-researcher", from: "Council", to: "Researcher", d: "M333 218 C390 244 397 362 458 384", delay: 0.95, duration: 4.4 },
  { id: "strategist-archives", from: "Strategist", to: "Archives", d: "M526 64 C596 69 625 112 682 100", delay: 1.1, duration: 4.6 },
  { id: "scholar-evidence", from: "Scholar", to: "Evidence", d: "M525 218 C586 218 633 218 683 218", delay: 1.35, duration: 4 },
  { id: "researcher-records", from: "Researcher", to: "Records", d: "M526 384 C592 383 641 354 683 360", delay: 1.55, duration: 4.5 },
  { id: "archives-documents", from: "Archives", to: "Documents", d: "M750 100 C815 100 844 64 891 64", delay: 1.85, duration: 4.3 },
  { id: "evidence-cli", from: "Evidence", to: "CLI", d: "M749 218 C812 213 840 158 891 156", delay: 2, duration: 4.2 },
  { id: "evidence-api", from: "Evidence", to: "API", d: "M749 218 C810 226 846 271 891 270", delay: 2.2, duration: 4.2 },
  { id: "records-mcp", from: "Records", to: "MCP", d: "M749 360 C812 359 844 405 891 405", delay: 2.45, duration: 4.8 },
];

const toneStyles = {
  white: {
    stroke: "rgba(255,255,255,0.92)",
    glow: "rgba(255,255,255,0.28)",
    fill: "rgba(255,255,255,0.055)",
  },
  green: {
    stroke: "#75efad",
    glow: "rgba(94,210,156,0.34)",
    fill: "rgba(94,210,156,0.08)",
  },
  cyan: {
    stroke: "#22d3ee",
    glow: "rgba(34,211,238,0.28)",
    fill: "rgba(34,211,238,0.07)",
  },
};

function isConnected(edge: Edge, active: NodeKey | null) {
  return active ? edge.from === active || edge.to === active : false;
}

function NetworkGraph() {
  const [activeNode, setActiveNode] = useState<NodeKey | null>(null);
  const reduceMotion = useReducedMotion();
  const animateDetails = !reduceMotion && !(typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches);

  const connectedNodes = useMemo(() => {
    if (!activeNode) return new Set<NodeKey>();
    const set = new Set<NodeKey>([activeNode]);
    edges.forEach((edge) => {
      if (edge.from === activeNode) set.add(edge.to);
      if (edge.to === activeNode) set.add(edge.from);
    });
    return set;
  }, [activeNode]);

  return (
    <div className="relative h-full min-h-0 w-full overflow-hidden rounded-[24px] bg-[#070b0a] lg:min-h-[680px] lg:rounded-[28px]">
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(94,210,156,0.055)_1px,transparent_1px),linear-gradient(90deg,rgba(94,210,156,0.05)_1px,transparent_1px)] bg-[size:34px_34px] sm:bg-[size:44px_44px]" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_48%_48%,rgba(34,211,238,0.12),transparent_24%),radial-gradient(circle_at_72%_50%,rgba(94,210,156,0.10),transparent_25%),radial-gradient(circle_at_18%_44%,rgba(94,210,156,0.08),transparent_22%)]" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_40%,rgba(0,0,0,0.56)_100%)]" />

      <svg className="relative z-10 h-full w-full" viewBox="0 0 1000 500" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Council methodology network">
        <defs>
          <filter id="mc-network-glow" x="-80%" y="-80%" width="260%" height="260%">
            <feGaussianBlur stdDeviation="8" result="blur" />
            <feColorMatrix in="blur" type="matrix" values="0 0 0 0 0.36 0 0 0 0 0.82 0 0 0 0 0.61 0 0 0 0.7 0" />
            <feMerge>
              <feMergeNode />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="mc-soft-node-glow" x="-130%" y="-130%" width="360%" height="360%">
            <feGaussianBlur stdDeviation="15" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <g>
          {edges.map((edge) => {
            const highlighted = isConnected(edge, activeNode);
            return (
              <g key={edge.id}>
                <motion.path
                  d={edge.d}
                  fill="none"
                  stroke={highlighted ? "rgba(117,239,173,0.86)" : "rgba(94,210,156,0.42)"}
                  strokeWidth={highlighted ? 1.45 : 1.05}
                  strokeLinecap="round"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: highlighted ? 1 : 0.72 }}
                  transition={{ duration: 1.2, delay: edge.delay * 0.18, ease: [0.16, 1, 0.3, 1] }}
                  style={{
                    filter: highlighted ? "drop-shadow(0 0 7px rgba(94,210,156,0.48))" : "drop-shadow(0 0 3px rgba(34,211,238,0.16))",
                  }}
                />
                {animateDetails && (
                  <>
                    <circle r={2.7} fill="#78f2b1" opacity={highlighted ? 1 : 0.74} filter="url(#mc-network-glow)">
                      <animateMotion dur={`${edge.duration}s`} begin={`${edge.delay}s`} repeatCount="indefinite" path={edge.d} />
                    </circle>
                    <circle r={1.7} fill="#22d3ee" opacity={highlighted ? 0.85 : 0.42}>
                      <animateMotion dur={`${edge.duration * 1.22}s`} begin={`${edge.delay + 1.4}s`} repeatCount="indefinite" path={edge.d} />
                    </circle>
                  </>
                )}
              </g>
            );
          })}
        </g>

        <g>
          {nodes.map((node) => {
            const tone = toneStyles[node.tone];
            const highlighted = activeNode ? connectedNodes.has(node.id) : false;
            return (
              <motion.g
                key={node.id}
                onMouseEnter={() => setActiveNode(node.id)}
                onMouseLeave={() => setActiveNode(null)}
                initial={{ opacity: 0, scale: 0.86 }}
                animate={{
                  opacity: activeNode && !highlighted ? 0.58 : 1,
                  scale: highlighted ? 1.075 : 1,
                  y: animateDetails ? [0, node.id === "Council" ? -2 : -4, 0] : 0,
                }}
                transition={{
                  opacity: { duration: 0.25 },
                  scale: { duration: 0.28, ease: [0.16, 1, 0.3, 1] },
                  y: { duration: 4.8 + node.delay, repeat: Infinity, ease: "easeInOut", delay: node.delay },
                  delay: node.delay * 0.05,
                }}
                style={{ transformOrigin: `${node.x}px ${node.y}px`, cursor: "pointer", willChange: "transform" }}
              >
                <circle cx={node.x} cy={node.y} r={node.r + 22} fill={tone.glow} opacity={highlighted ? 0.54 : 0.28} filter="url(#mc-soft-node-glow)" />
                <motion.circle
                  cx={node.x}
                  cy={node.y}
                  r={node.r + 7}
                  fill="none"
                  stroke={tone.stroke}
                  strokeWidth={1.5}
                  opacity={highlighted ? 0.78 : 0.42}
                  animate={animateDetails ? { scale: [0.94, 1.06, 0.94], opacity: highlighted ? [0.65, 0.95, 0.65] : [0.34, 0.56, 0.34] } : undefined}
                  transition={{ duration: 3.3, repeat: Infinity, ease: "easeInOut", delay: node.delay }}
                  style={{ transformOrigin: `${node.x}px ${node.y}px` }}
                />
                <circle cx={node.x} cy={node.y} r={node.r} fill="rgba(8,22,17,0.88)" stroke={tone.stroke} strokeWidth={node.id === "Council" ? 1.6 : 1.35} />
                <circle cx={node.x} cy={node.y} r={node.r - 9} fill={tone.fill} opacity={highlighted ? 0.55 : 0.34} />
                <text
                  x={node.x}
                  y={node.y + 5}
                  textAnchor="middle"
                  fill="#ffffff"
                  fontFamily="Inter, ui-sans-serif, system-ui, sans-serif"
                  fontSize={node.id === "Council" ? 18 : 14}
                  fontWeight={800}
                  letterSpacing={0}
                >
                  {node.id}
                </text>
              </motion.g>
            );
          })}
        </g>

        {animateDetails && (
          <g opacity="0.82">
            {[
              [210, 218, 2.6, 0.2],
              [410, 218, 2.3, 1.2],
              [596, 218, 2.4, 0.6],
              [318, 360, 2.5, 1.5],
              [845, 64, 2.4, 0.8],
              [823, 405, 2.3, 1.8],
            ].map(([x, y, r, delay]) => (
              <motion.circle
                key={`${x}-${y}`}
                cx={x}
                cy={y}
                r={r}
                fill="#75efad"
                animate={{ opacity: [0.2, 0.9, 0.2], scale: [0.85, 1.25, 0.85] }}
                transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut", delay }}
              />
            ))}
          </g>
        )}
      </svg>
    </div>
  );

}

export default memo(NetworkGraph);
